import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Simple from './Simple';
import Destination from './Destination';
import PipelineExperiment from './PipelineExperiment';
import PrimaryButton from '../../../../components/PrimaryButton';
import { GenericSelect, SelectI } from '../../components/Select';
import recruitmentTypes from '../../../../fixtures/recruitment/types';
import {
  GlobalFormData,
  RecruitmentSimple,
  RecruitmentDestination,
  PipelineExperiment as PipelineExperimentType,
  Recruitment as LocalData
} from '../../../../types/conf';
import { Notyf } from 'notyf';
import useCreateStudyConf from '../../hooks/useCreateStudyConf';
import ConfWrapper from '../../components/ConfWrapper';
const Select = GenericSelect as SelectI<any>;

const getStartOfMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01T00:00`;
};

const getEndOfMonth = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59`;
};

interface Props {
  id: string;
  globalData: GlobalFormData;
  localData: any;
}

const duckTypeRecruitmentType = (localData: any) => {
  if (localData?.arms) {
    return 'pipeline_experiment'
  } else if (localData?.ad_campaign_name_base) {
    return 'destination'
  } else {
    return 'simple'
  }
}

const Recruitment: React.FC<Props> = ({
  id,
  globalData,
  localData,
}: Props) => {

  const [recruitmentType, setRecruitmentType] = useState<string>(duckTypeRecruitmentType(localData));

  const initialState: any[] = [
    {
      end_date: getEndOfMonth(),
      start_date: getStartOfMonth(),
      ad_campaign_name: '',
      objective: '',
      optimization_goal: '',
      destination_type: '',
      min_budget: 2,
      budget: '',
      max_sample: '',
      type: 'simple',
      incentive_per_respondent: 0,
    },
    {
      ad_campaign_name_base: '',
      objective: '',
      optimization_goal: '',
      destination_type: '',
      min_budget: 2,
      budget_per_arm: '',
      end_date: getEndOfMonth(),
      start_date: getStartOfMonth(),
      max_sample_per_arm: '',
      arms: '',
      recruitment_days: '',
      offset_days: '',
      type: 'pipeline_experiment',
      incentive_per_respondent: 0,
    },
    {
      destination: '',
      ad_campaign_name_base: '',
      objective: '',
      optimization_goal: '',
      destination_type: '',
      min_budget: 2,
      budget_per_arm: '',
      end_date: getEndOfMonth(),
      start_date: getStartOfMonth(),
      max_sample_per_arm: '',
      type: 'destination',
      incentive_per_respondent: 0,
    },
  ];

  const [formData, setFormData] = useState<LocalData>(
    localData ? localData : initialState.find((obj: any) => obj.type === recruitmentType)
  );

  const handleSelectChange = (e: any) => {
    const { value } = e.target;
    setRecruitmentType(value);
    const fields = initialState.find((obj: any) => obj.type === value);
    setFormData(fields);
  };

  const params = useParams<{ studySlug: string }>();

  const studySlug = params.studySlug;

  const { createStudyConf, isLoadingOnCreateStudyConf } = useCreateStudyConf(
    'Recruitment settings saved',
    studySlug,
    'recruitment'
  );


  const formatData = (data: LocalData) => {
    if ("ad_campaign_name" in data) {
      data.ad_campaign_name = data.ad_campaign_name.trim()
    }

    if ("ad_campaign_name_base" in data) {
      data.ad_campaign_name_base = data.ad_campaign_name_base.trim()
    }

    return data;
  };

  const onSubmit = (e: any): void => {
    e.preventDefault();
    const notyf = new Notyf();
    const data = formatData(formData) as any;

    const minBudget = data.min_budget || 0;
    if (minBudget < 2) {
      notyf.error({
        message: 'Minimum budget must be at least $2.00 per ad set (Facebook requirement).',
        dismissible: true,
        duration: 8000,
      });
      return;
    }

    createStudyConf({ data, studySlug, confType: id });
  };

  return (
    <ConfWrapper>
      <form onSubmit={onSubmit}>
        <Select
          name="destination"
          options={recruitmentTypes}
          handleChange={handleSelectChange}
          value={recruitmentType}
          label="Select a recruitment type"
        ></Select>
        {recruitmentType === 'simple' && (
          <Simple formData={formData as RecruitmentSimple} updateFormData={setFormData} />
        )}
        {recruitmentType === 'pipeline_experiment' && (
          <PipelineExperiment
            formData={formData as PipelineExperimentType}
            updateFormData={setFormData}
          />
        )}
        {recruitmentType === 'destination' && (
          <Destination
            formData={formData as RecruitmentDestination}
            updateFormData={setFormData}
            destinations={globalData.destinations}
            studySlug={studySlug}
          />
        )}
        <div className="p-6 text-right">
          <PrimaryButton
            leftIcon="CheckCircleIcon"
            type="submit"
            testId="form-submit-button"
            loading={isLoadingOnCreateStudyConf}
          >
            Next
          </PrimaryButton>
        </div>
      </form>
    </ConfWrapper>
  );
};

export default Recruitment;
